import React, { useState, useEffect } from "react";
import { Company } from "../../../interfaces/Company";
import api from "../../../services/axios";
import { GetHours } from "../../../interfaces/GetHours";
import EditHourModal from "./components/EditHourModal";
import HourEntry from "./components/HourEntry";
import { Project } from "../../../interfaces/Project";
import SkeletonTable from "../../../components/skeleton/table";
import getDaysInMonth from "../../../utils/dateUtils";
import Card from "../../../components/card";
import { MdSearch, MdAddCircleOutline, MdAccessTimeFilled } from "react-icons/md";
import SearchableSelect from "../../../components/searchableSelect/SearchableSelect";

const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const TimeSheetManagement = () => {
  // const [selectedButton, setSelectedButton] = useState<string>("hours");
  const [companyList, setCompanies] = useState<Company[]>([]);
  const [projectList, setProject] = useState<Project[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isHourModalOpen, setIsHourModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  const [data, setData] = useState<GetHours[]>();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);

  const [selectedCompany, setSelectedCompany] = useState<number>(0);

  const [daysInMonth, setDaysInMonth] = useState<number[]>(
    getDaysInMonth(selectedYear, selectedMonth)
  );

  useEffect(() => {
    api
      .get("/EmployeeClientProject/search")
      .then((response) => {
        const active = response.data.filter((p: any) => p.flActive);

        const uniqueClients = Array.from(
          new Map(active.map((p: any) => [p.clientId, p])).values()
        ).map((p: any) => ({
          id: p.clientId,
          legalName: p.nmCorporate,
          jiraProjectId: p.jiraProjectId,
        }));
        setCompanies(uniqueClients as Company[]);

        const mapped = active.map((p: any) => ({
          projectId: p.projectId,
          nmProject: p.nmProject,
          clientId: p.clientId,
          timeSheetId: 0,
        }));
        setProject(mapped);
      })
      .catch((error) => {
        console.error("There was an error fetching employee projects!", error);
      });
  }, []);

  const getWorkedHours = async (
    month: Number,
    year: Number,
    clientId: number | null
  ) => {
    setLoading(true);
    setTimeout(() => {
      api
        .get(
          `/TimeManagement/GetWorkedHours?month=${month}&year=${year}&clientId=${clientId}`
        )
        .then((response) => {
          setData(response.data);
          setLoading(false);
          setIsModalOpen(true);
        })
        .catch((error) => {
          console.error("There was an error!", error);
          setLoading(false);
        });
    }, 1000);
  };

  const onSearch = () => {
    setIsModalOpen(false);
    setLoading(true);
    getWorkedHours(selectedMonth, selectedYear, selectedCompany);
  };

  const openHourModal = () => {
    setIsHourModalOpen(true);
  };

  const onUpdateSearch = () => {
    setIsModalOpen(false);
    setLoading(true);
    getWorkedHours(selectedMonth, selectedYear, selectedCompany);
  };

  const handleSetYear = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(event.target.value));
  };

  const handleSetMonth = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(parseInt(event.target.value));
    setDaysInMonth(getDaysInMonth(selectedYear, parseInt(event.target.value)));
  };

  const closeHourModal = () => {
    setIsHourModalOpen(false);
  };

  return (
    <>
      {loading ? (
        <SkeletonTable active={loading} />
      ) : (
        <div className="mt-5 flex animate-fade-up flex-col gap-5">
          <div className="flex items-center gap-4">
            <span
              className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
              aria-hidden="true"
            >
              <MdAccessTimeFilled className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <span className="inline-flex items-center rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
                Apontamento
              </span>
              <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
                Folha de horas
              </h1>
            </div>
          </div>

          <Card extra="p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="year" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ano</label>
                <select id="year" className={selectCls} onChange={handleSetYear} value={selectedYear}>
                  {[2023, 2024, 2025, 2026].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="month" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Mês</label>
                <select id="month" className={selectCls} value={selectedMonth} onChange={handleSetMonth}>
                  {MONTH_LABELS.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="client" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Cliente</label>
                <SearchableSelect
                  placeholder="Todos"
                  value={selectedCompany ? selectedCompany : null}
                  onChange={(v) => setSelectedCompany(v == null ? 0 : Number(v))}
                  options={companyList.map((company) => ({ label: company.legalName, value: company.id }))}
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={openHourModal}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
              >
                <MdAddCircleOutline className="h-4 w-4" aria-hidden="true" />
                Lançar horas
              </button>
              <button
                type="button"
                onClick={onSearch}
                disabled={loading}
                className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-6 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                <MdSearch className="h-4 w-4" aria-hidden="true" />
                {loading ? "Processando…" : "Buscar"}
              </button>
            </div>
          </Card>
        </div>
      )}

      {isModalOpen && (
        <HourEntry
          data={data || []}
          daysOfMonth={daysInMonth}
          year={selectedYear}
          month={selectedMonth}
          companies={companyList || []}
          project={projectList || []}
          onUpdate={onUpdateSearch}
        />
      )}

      {isHourModalOpen && (
        <EditHourModal
          isModalOpen={isHourModalOpen}
          day={currentDay}
          month={currentMonth}
          year={currentYear}
          timeSheetid={0}
          timeSheetDetailId={0}
          dsActivity=""
          initialValue={"00:00"}
          company={companyList || []}
          initialCompanyValue={selectedCompany}
          project={projectList}
          initialProjectValue={0}
          onClose={closeHourModal}
          onUpdate={onUpdateSearch}
        />
      )}
    </>
  );
};

export default TimeSheetManagement;
